import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Plus, X } from "lucide-react";
import { get, patch, post } from "@/lib/api";

const STATUSES = ["active", "paused", "completed", "refunded"];
const fmtMoney = (v) =>
    new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(v || 0);

const STATUS_COLOR = {
    active: "text-[#22d3ee] border-[#22d3ee]/40 bg-[#05151a]",
    paused: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#181301]",
    completed: "text-[#a3b5d1] border-[#1a2a4a] bg-[#0a1018]",
    refunded: "text-[#fca5a5] border-[#fca5a5]/40 bg-[#1a0606]",
};

export default function Clients() {
    const [rows, setRows] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [paymentOpen, setPaymentOpen] = useState(null);
    const [payForm, setPayForm] = useState({
        amount_usd: "",
        method: "stripe",
        paid_at: new Date().toISOString().slice(0, 10),
        note: "",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, p] = await Promise.all([
                get("/admin/clients"),
                get("/admin/payments"),
            ]);
            setRows(c);
            setPayments(p);
        } catch {
            toast.error("Failed to load clients");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const totalPaid = (clientId) =>
        payments
            .filter((p) => p.client_id === clientId)
            .reduce((s, p) => s + (p.amount_usd || 0), 0);

    const setStatus = async (c, status) => {
        setSavingId(c.id);
        try {
            const next = await patch(`/admin/clients/${c.id}`, { status });
            setRows((r) => r.map((x) => (x.id === c.id ? next : x)));
            toast.success(`Status: ${status}`);
        } catch {
            toast.error("Update failed");
        } finally {
            setSavingId(null);
        }
    };

    const addPayment = async () => {
        if (!paymentOpen) return;
        const amt = parseFloat(payForm.amount_usd);
        if (!amt || amt <= 0) {
            toast.error("Enter a valid amount");
            return;
        }
        try {
            setSavingId(paymentOpen.id);
            await post("/admin/payments", {
                client_id: paymentOpen.id,
                amount_usd: amt,
                method: payForm.method,
                paid_at: payForm.paid_at,
                note: payForm.note,
            });
            toast.success("Payment logged");
            setPaymentOpen(null);
            setPayForm({
                amount_usd: "",
                method: "stripe",
                paid_at: new Date().toISOString().slice(0, 10),
                note: "",
            });
            await load();
        } catch {
            toast.error("Could not log payment");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-clients">
            <header className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                    Roster
                </p>
                <h1 className="mt-2 font-display font-black text-white text-3xl md:text-4xl tracking-tight">
                    Clients
                </h1>
                <p className="mt-2 text-white/55 text-sm">
                    Paying clients in the 90 Day Alpha program. Track status,
                    log payments, manage the roster.
                </p>
            </header>

            <div className="bg-[#060c1e] border border-[#1a2a4a] overflow-hidden">
                <div className="grid grid-cols-12 px-5 py-3 border-b border-[#1a2a4a] text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-3 hidden md:block">Contact</div>
                    <div className="col-span-2">Plan</div>
                    <div className="col-span-2">Paid / Owed</div>
                    <div className="col-span-2">Status</div>
                </div>

                {loading && (
                    <div className="px-5 py-12 text-white/55 text-sm flex items-center gap-3">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="px-5 py-12 text-white/55 text-sm">
                        No clients yet. Convert a prospect from the Prospects tab.
                    </div>
                )}
                {!loading &&
                    rows.map((c) => {
                        const paid = totalPaid(c.id);
                        const owed = Math.max(0, (c.plan_price_usd || 0) - paid);
                        const color = STATUS_COLOR[c.status] || STATUS_COLOR.active;
                        return (
                            <div
                                key={c.id}
                                data-testid={`client-row-${c.id}`}
                                className="grid grid-cols-12 px-5 py-4 items-center border-b border-[#142142] last:border-b-0 hover:bg-[#0a1429]/40 transition-colors"
                            >
                                <div className="col-span-3">
                                    <p className="text-white font-semibold text-sm">
                                        {c.name}
                                    </p>
                                    <p className="text-white/50 text-[11px]">
                                        Since{" "}
                                        {new Date(
                                            c.start_date
                                        ).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="col-span-3 hidden md:block">
                                    <p className="text-white/85 text-xs flex items-center gap-1.5">
                                        <Mail size={11} />
                                        {c.email}
                                    </p>
                                    <p className="text-white/55 text-[11px] flex items-center gap-1.5 mt-1">
                                        <Phone size={11} />
                                        {c.phone}
                                    </p>
                                </div>
                                <div className="col-span-2 text-white/85 text-xs">
                                    <p className="font-semibold">{c.plan}</p>
                                    <p className="text-white/50">
                                        {fmtMoney(c.plan_price_usd)}
                                    </p>
                                </div>
                                <div className="col-span-2 text-white/85 text-xs">
                                    <p className="text-[#00B7FF] font-semibold">
                                        {fmtMoney(paid)}
                                    </p>
                                    {owed > 0 ? (
                                        <p className="text-[#fca5a5] text-[11px]">
                                            {fmtMoney(owed)} owed
                                        </p>
                                    ) : (
                                        <p className="text-white/45 text-[11px]">
                                            Paid in full
                                        </p>
                                    )}
                                </div>
                                <div className="col-span-2">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <span
                                            className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold border ${color}`}
                                        >
                                            {c.status}
                                        </span>
                                        <select
                                            data-testid={`client-status-${c.id}`}
                                            disabled={savingId === c.id}
                                            value={c.status}
                                            onChange={(e) =>
                                                setStatus(c, e.target.value)
                                            }
                                            className="bg-[#020617] border border-[#1a2a4a] text-white/85 text-[11px] px-2 py-1 focus:border-[#1E90FF] outline-none"
                                        >
                                            {STATUSES.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            data-testid={`add-payment-${c.id}`}
                                            onClick={() => setPaymentOpen(c)}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37] hover:text-black transition-all"
                                        >
                                            <Plus size={11} /> Payment
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
            </div>

            {paymentOpen && (
                <div
                    data-testid="payment-modal"
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-5"
                    onClick={() => setPaymentOpen(null)}
                >
                    <div
                        className="bg-[#060c1e] border border-[#1a2a4a] max-w-md w-full p-7"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00B7FF]">
                                    Log Payment
                                </p>
                                <h3 className="mt-2 font-display font-black text-white text-2xl tracking-tight">
                                    {paymentOpen.name}
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPaymentOpen(null)}
                                className="text-white/55 hover:text-white"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="mt-6 space-y-4">
                            <FInput
                                testId="payment-amount"
                                label="Amount (USD)"
                                type="number"
                                value={payForm.amount_usd}
                                onChange={(v) =>
                                    setPayForm((f) => ({ ...f, amount_usd: v }))
                                }
                            />
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-white/65 mb-1.5">
                                    Method
                                </label>
                                <select
                                    data-testid="payment-method"
                                    value={payForm.method}
                                    onChange={(e) =>
                                        setPayForm((f) => ({
                                            ...f,
                                            method: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] outline-none text-white px-3 py-2.5 text-sm"
                                >
                                    <option value="stripe">Stripe</option>
                                    <option value="bank">Bank transfer</option>
                                    <option value="paypal">PayPal</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <FInput
                                testId="payment-date"
                                label="Paid on"
                                type="date"
                                value={payForm.paid_at}
                                onChange={(v) =>
                                    setPayForm((f) => ({ ...f, paid_at: v }))
                                }
                            />
                            <FInput
                                testId="payment-note"
                                label="Note (optional)"
                                value={payForm.note}
                                onChange={(v) =>
                                    setPayForm((f) => ({ ...f, note: v }))
                                }
                            />
                        </div>
                        <button
                            type="button"
                            data-testid="payment-confirm"
                            disabled={savingId === paymentOpen.id}
                            onClick={addPayment}
                            className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-[#C5A059] transition-all disabled:opacity-50"
                        >
                            {savingId === paymentOpen.id ? (
                                <Loader2 className="animate-spin" size={14} />
                            ) : null}
                            Save Payment
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function FInput({ testId, label, value, onChange, type = "text" }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-white/65 mb-1.5">
                {label}
            </label>
            <input
                data-testid={testId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] outline-none text-white px-3 py-2.5 text-sm"
            />
        </div>
    );
}
